import Vue from 'vue';

/**
 * Placement and harvest fields refreshed after a Set / Clear / Unharvest write.
 *
 * A whitelist, not a whole-object assign: the refetch (`GET_BASE_TAG_BY_ID` →
 * `api/tags/getBaseTagById`) loads only the Children navigation, and the client model
 * constructor defaults comments / attachments / documents / linkedTags1 / linkedTags2 to `[]`
 * (forgeModels.js ForgeBaseTag constructor). Copying the whole refreshed tag would wipe those
 * relations from every visible copy, including the open details panel.
 *
 * Every name is read by a placement gate (TagCard canZoom, canSetPosition, canShowMasterClear,
 * canUnharvest, harvestStatus, isPositionedInCurrentFacility, isolate; isCurrentFacilityHarvested)
 * or by a placement renderer / navigation reader:
 *   - point, children, thumbnailUrl, linked360ImageInfo: the placement itself and its display
 *     (linked360ImageInfo is read by NAVIGATE_TAG_SPATIALLY and getNearest360Image).
 *   - spatialTargets: authoritative destinations; undeclared in the constructor.
 *   - positionedFacilityIds, otherPositionedFacilityIds, positionImage360Id: per-facility position.
 *   - availableAssets, otherAvailableAssets, isHarvestedInCurrentFacility: derived from children
 *     by processChildrenForFacilityGroup.
 *   - hasCurrentFacilityHarvest, tagHarvesterId: harvest ownership (Unharvest nulls the
 *     harvester id server-side; isCurrentFacilityHarvested falls back to it).
 *   - heightFromDeck: derived PURELY from the point, and recomputed inside the AVA Inspect
 *     position write (AvaInspectTagsController.RecalculateHeightFromDeckAsync), so without it the
 *     info panel keeps rendering the height of the previous position.
 *
 * `deckBelowElevation` is deliberately NOT here: it is a manual field the user types into the AVA
 * Inspect edit dialog ("Deck Below Elevation (manual entry)"), owned by that dialog's save.
 */
export const TAG_PLACEMENT_FIELDS = Object.freeze([
  'point',
  'children',
  'thumbnailUrl',
  'spatialTargets',
  'positionedFacilityIds',
  'otherPositionedFacilityIds',
  'positionImage360Id',
  'linked360ImageInfo',
  'hasCurrentFacilityHarvest',
  'isHarvestedInCurrentFacility',
  'availableAssets',
  'otherAvailableAssets',
  'tagHarvesterId',
  'heightFromDeck',
]);

/**
 * Copies only the placement fields from an authoritative refresh onto a visible tag copy.
 *
 * - `undefined` on the source is skipped (the response did not carry the field).
 * - `null` is copied, so a cleared position clears.
 * - Arrays are shallow-copied, so no two copies share one array.
 * - Existing keys use plain assignment (class setters run); missing keys use `Vue.set`.
 *
 * @param {Object} target - Visible tag copy, patched in place.
 * @param {Object} source - Refreshed tag from the server.
 * @returns {boolean} false when either side is missing or the ids differ.
 */
export function applyTagPlacementFields(target, source) {
  if (!target || !source) return false;
  if (source.id == null || String(target.id) !== String(source.id)) return false;

  TAG_PLACEMENT_FIELDS.forEach(field => {
    const value = source[field];
    if (value === undefined) return;

    const next = Array.isArray(value) ? [...value] : value;
    if (field in target) {
      target[field] = next;
    } else {
      Vue.set(target, field, next);
    }
  });
  return true;
}
